import PropTypes from "prop-types";
import { useRef, useState } from "react";

// material-ui
import {
  Avatar,
  Badge,
  Box,
  Button,
  ButtonBase,
  CardContent,
  ClickAwayListener,
  Divider,
  Grid,
  Paper,
  Popper,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// project import
import Transitions from "../../../../components/@extended/Transitions";
import MainCard from "../../../../components/MainCard";
import ProfileTab from "./ProfileTab";

// assets
// import { LogoutOutlined, SettingOutlined, UserOutlined } from '@ant-design/icons';

import { Person } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { properlizeWords, startWithUpperCase } from "../../../../helpers";
import { tryLogout } from "../../../../stateManagement/reducers/authReducer";

// tab panel wrapper
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `profile-tab-${index}`,
    "aria-controls": `profile-tabpanel-${index}`,
  };
}

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

const Profile = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const dispatch = useDispatch();
  const { authToken } = useSelector((state) => state.auth);

  const handleLogout = async () => {
    // logout
    dispatch(tryLogout({ authToken: authToken ?? "" }));
  };

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const [value, setValue] = useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const iconBackColorOpen = "grey.300";

  const { user } = useSelector((state) => state.auth);

  const StyledBadge = styled(Badge)(({ theme }) => ({
    "& .MuiBadge-badge": {
      backgroundColor: "#44b700",
      color: "#44b700",
      boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
      "&::after": {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        borderRadius: "50%",
        animation: "ripple 1.2s infinite ease-in-out",
        border: "1px solid currentColor",
        content: '""',
      },
    },
    "@keyframes ripple": {
      "0%": {
        transform: "scale(.8)",
        opacity: 1,
      },
      "100%": {
        transform: "scale(2.4)",
        opacity: 0,
      },
    },
  }));

  return (
    <Box
      sx={{
        flexShrink: 0,
        ml: "0 !important",
        position: "relative",
        zIndex: 10000000000000,
      }}
    >
      <ButtonBase
        sx={{
          p: 0.25,
          bgcolor: open ? "transparent" : "transparent",
          borderRadius: 1,
        }}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? "profile-grow" : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 0.5 }}>
          <StyledBadge
            overlap="circular"
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            variant="dot"
          >
            <Avatar
              variant="circular"
              sx={{
                height: "2.25rem",
                width: "2.25rem",
                backgroundColor: "#faf9f6b8 !important",
                backdropFilter: "blur(4px) saturate(140%)",

                "& img": {
                  width: "100% !important",
                  height: "100% !important",
                  objectFit: "cover !important",
                },
              }}
              alt="profile user"
              src={
                user?.imgSrc
                  ? `${process.env.REACT_APP_SERVER_ENDPOINT}/${user?.imgSrc}`
                  : ""
              }
            >
              <Person />
            </Avatar>
            {/* <Typography variant="subtitle1">{`${startWithUpperCase(user?.firstName ?? '')} ${startWithUpperCase(user?.lastName ?? '')}`}</Typography> */}
          </StyledBadge>
        </Stack>
      </ButtonBase>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 9],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="fade" in={open} {...TransitionProps}>
            {open && (
              <Paper
                sx={{
                  boxShadow: "inherit",
                  width: 290,
                  minWidth: 240,
                  maxWidth: 290,
                  [theme.breakpoints.down("md")]: {
                    maxWidth: 250,
                  },
                  backgroundColor: "transparent !important",
                }}
              >
                <ClickAwayListener onClickAway={handleClose}>
                  <MainCard
                    shadow="xl"
                    boxShadow={true}
                    className="shadow"
                    elevation={0}
                    border={false}
                    content={false}
                    sx={{
                      backgroundColor: "#faf9f6b8 !important",
                      backdropFilter: "blur(4px) saturate(140%)",
                    }}
                  >
                    <CardContent
                      sx={{ px: { xs: 2, md: 6 }, pt: 3, pb: ".5rem" }}
                    >
                      <Grid
                        container
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Grid item>
                          <Stack
                            direction="row"
                            spacing={1.25}
                            alignItems="center"
                          >
                            <Avatar
                              alt="profile user"
                              src={
                                user?.imgSrc
                                  ? `${process.env.REACT_APP_SERVER_ENDPOINT}/${user?.imgSrc}`
                                  : ""
                              }
                              sx={{
                                "& img": {
                                  width: "100% !important",
                                  height: "100% !important",
                                  objectFit: "cover !important",
                                },
                                width: 32,
                                height: 32,
                                backgroundColor: "#faf9f6b8 !important",
                                backdropFilter: "blur(4px) saturate(140%)",
                              }}
                            >
                              <Person />
                            </Avatar>
                            <Stack>
                              <Typography
                                variant="h6"
                                className="!text-primary"
                              >{`${startWithUpperCase(user?.firstName ?? "")} ${startWithUpperCase(user?.lastName ?? "")}`}</Typography>
                              <Typography variant="body2" color="textSecondary">
                                {properlizeWords(user?.role?.name ?? "")}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Grid>
                        {/* <Grid item>
                          <IconButton size="large" color="secondary" onClick={handleLogout}>
                            <LogoutOutlined />
                          </IconButton>
                        </Grid> */}
                      </Grid>
                    </CardContent>
                    <Divider
                      textAlign="center"
                      sx={{
                        mx: "auto",
                        my: 0,
                        backgroundColor: `${theme.palette.background.default}`,
                      }}
                    />
                    {open && (
                      <>
                        <TabPanel value={value} index={0} dir={theme.direction}>
                          <ProfileTab handleLogout={handleLogout} />
                        </TabPanel>
                      </>
                    )}
                    <Stack
                      alignItems="center"
                      sx={{
                        width: "100%",
                      }}
                    >
                      <Button
                        variant="contained"
                        size="large"
                        sx={{
                          backgroundColor: theme.palette.error.main,
                          borderColor: theme.palette.error.main,
                          my: 2,

                          "&:hover": {
                            backgroundColor: theme.palette.error.dark,
                            borderColor: theme.palette.error.dark,
                          },
                        }}
                        onClick={() => {
                          dispatch(tryLogout({ authToken: authToken ?? "" }));
                        }}
                      >
                        <b>LOG OUT</b>
                      </Button>
                    </Stack>
                  </MainCard>
                </ClickAwayListener>
              </Paper>
            )}
          </Transitions>
        )}
      </Popper>
    </Box>
  );
};

export default Profile;
